// frontend/src/utils/exportExcel.js
import * as XLSX from 'xlsx';

/**
 * Exporte des données en fichier Excel
 * @param {Array} data - Tableau d'objets à exporter
 * @param {Array} columns - [{key, label, format}] définition des colonnes
 * @param {string} filename - Nom du fichier (sans extension)
 * @param {string} sheetName - Nom de l'onglet
 */
export function exportToExcel(data, columns, filename, sheetName = 'Données') {
  // Préparer les données avec les en-têtes
  const headers = columns.map(col => col.label);
  
  const rows = data.map(item =>
    columns.map(col => {
      const val = item[col.key];
      if (col.format === 'currency' && val !== null && val !== undefined) {
        return parseFloat(val) || 0;
      }
      if (col.format === 'date' && val) {
        return val.substring(0, 10);
      }
      if (val === null || val === undefined) return '';
      return val;
    })
  );

  // Créer le workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Style des en-têtes (largeur auto)
  const colWidths = columns.map(col => ({
    wch: Math.max(col.label.length, 15)
  }));
  ws['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Télécharger
  const date = new Date().toISOString().substring(0, 10);
  XLSX.writeFile(wb, `${filename}_${date}.xlsx`);
}

// ── Exports spécifiques ──────────────────────────────────────

export function exportClients(clients) {
  exportToExcel(clients, [
    { key: 'id',          label: 'ID' },
    { key: 'nom',         label: 'Nom' },
    { key: 'prenom',      label: 'Prénom' },
    { key: 'entreprise',  label: 'Entreprise' },
    { key: 'email',       label: 'Email' },
    { key: 'telephone',   label: 'Téléphone' },
    { key: 'adresse',     label: 'Adresse' },
    { key: 'code_postal', label: 'Code postal' },
    { key: 'ville',       label: 'Ville' },
    { key: 'created_at',  label: 'Date création', format: 'date' },
  ], 'clients', 'Clients');
}

export function exportChantiers(chantiers) {
  exportToExcel(chantiers, [
    { key: 'id',              label: 'ID' },
    { key: 'nom',             label: 'Nom chantier' },
    { key: 'client_nom',      label: 'Client' },
    { key: 'client_entreprise', label: 'Entreprise client' },
    { key: 'statut',          label: 'Statut' },
    { key: 'avancement',      label: 'Avancement (%)' },
    { key: 'adresse_chantier', label: 'Adresse chantier' },
    { key: 'date_debut',      label: 'Date début', format: 'date' },
    { key: 'date_fin_prevue', label: 'Date fin prévue', format: 'date' },
    { key: 'date_fin_reelle', label: 'Date fin réelle', format: 'date' },
    { key: 'budget',          label: 'Budget (€)', format: 'currency' },
  ], 'chantiers', 'Chantiers');
}

export function exportDevis(devis) {
  exportToExcel(devis, [
    { key: 'id',                  label: 'ID' },
    { key: 'numero_devis',        label: 'N° Devis' },
    { key: 'chantier_nom',        label: 'Chantier' },
    { key: 'client_nom',          label: 'Client' },
    { key: 'client_entreprise',   label: 'Entreprise client' },
    { key: 'statut',              label: 'Statut' },
    { key: 'date_creation',       label: 'Date création', format: 'date' },
    { key: 'date_validite',       label: 'Date validité', format: 'date' },
    { key: 'montant_ht',          label: 'Montant HT (€)', format: 'currency' },
    { key: 'tva',                 label: 'TVA (%)' },
    { key: 'montant_ttc',         label: 'Montant TTC (€)', format: 'currency' },
    { key: 'conditions_paiement', label: 'Conditions paiement' },
    { key: 'notes',               label: 'Notes' },
  ], 'devis', 'Devis');
}

export function exportFactures(factures) {
  exportToExcel(factures, [
    { key: 'id',               label: 'ID' },
    { key: 'numero_facture',   label: 'N° Facture' },
    { key: 'numero_devis',     label: 'N° Devis' },
    { key: 'chantier_nom',     label: 'Chantier' },
    { key: 'client_nom',       label: 'Client' },
    { key: 'client_entreprise', label: 'Entreprise client' },
    { key: 'statut',           label: 'Statut' },
    { key: 'date_emission',    label: 'Date émission', format: 'date' },
    { key: 'date_echeance',    label: 'Date échéance', format: 'date' },
    { key: 'date_paiement',    label: 'Date paiement', format: 'date' },
    { key: 'montant_ht',       label: 'Montant HT (€)', format: 'currency' },
    { key: 'tva',              label: 'TVA (%)' },
    { key: 'montant_ttc',      label: 'Montant TTC (€)', format: 'currency' },
    { key: 'notes',            label: 'Notes' },
  ], 'factures', 'Factures');
}

export function exportInterventions(interventions) {
  exportToExcel(interventions, [
    { key: 'id',           label: 'ID' },
    { key: 'titre',        label: 'Titre' },
    { key: 'chantier_nom', label: 'Chantier' },
    { key: 'type',         label: 'Type' },
    { key: 'statut',       label: 'Statut' },
    { key: 'date_debut',   label: 'Date début', format: 'date' },
    { key: 'date_fin',     label: 'Date fin', format: 'date' },
    { key: 'lieu',         label: 'Lieu' },
    { key: 'responsable',  label: 'Responsable' },
    { key: 'notes',        label: 'Notes' },
  ], 'interventions', 'Interventions');
}
